import React, { useEffect, useId, useRef, useState } from 'react';
import {
  AlertDialog, AlertDialogContent, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';

const EMPTY_COPIES = [];
const buttonClass = 'rounded-lg border px-3 py-2 text-sm disabled:opacity-50';
const isLegacy = (copy) => ['legacy', 'legacy-outbox'].includes(copy.source);
const operation = (copy) => copy.attempt?.kind === 'failure' || copy.source === 'legacy-failure' ? 'failure' : copy.kind;
const sourceLabel = (copy) => ({ tab: 'Running tab', active: 'Saved on this device', device: 'Saved on this device',
  alternative: 'Retained alternative', 'legacy-failure': 'Older failure work in this tab',
  legacy: 'Older saved copy', 'legacy-outbox': 'Older saved copy' }[copy.source] || 'Local copy');

function CopyDetails({ copy }) {
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    const urls = [];
    let failed = false;
    try {
      for (const photo of copy.fields?.photos || []) {
        const file = photo.prepared?.file || photo.file;
        if (file instanceof Blob && file.type.startsWith('image/')) urls.push(URL.createObjectURL(file));
      }
    } catch {
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.length = 0;
      failed = true;
    }
    setPreview({ copy, urls, failed });
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [copy]);
  const fields = copy.fields || {};
  return <div className="mt-2 space-y-2 break-words">
    <dl className="space-y-1 text-sm">
      {[
        ['Received by', fields.receivedBy], ['Relationship', fields.receivedByRelationship], ['Notes', fields.notes],
        ['Delivery time', fields.deliveryTime], ['Recorded delivery time', fields.deliveryTimeIso],
        ['Failure reason', fields.failureReason || fields.reason],
        ['Failure details', fields.failureDetails || fields.details],
      ].filter(([, value]) => !!value).map(([label, value]) => <div key={label}>
        <dt className="font-medium">{label}</dt><dd className="whitespace-pre-wrap">{value}</dd>
      </div>)}
      <div><dt className="font-medium">Signature</dt><dd>{fields.signatureData ? 'Present in this local copy' : 'None in this local copy'}</dd></div>
      <div><dt className="font-medium">Video</dt><dd>{fields.video ? 'Present in this local copy' : 'None in this local copy'}</dd></div>
    </dl>
    {preview?.copy === copy && preview.failed && <p role="alert">Preview unavailable. The saved photos have not been changed.</p>}
    {preview?.copy === copy && preview.urls.map((url, index) => <img key={url} src={url}
      alt={`Selected local copy, photo ${index + 1}`} className="max-h-64 max-w-full rounded object-contain" />)}
  </div>;
}

// TCA-26102306: presentation only. The coordinator owns authority and durable actions.
export default function SavedPhotoRecoveryPanel({ state, onRefresh, onKeepBoth, onSelect, onDiscard, onDiscardTab, onCheckReceipt, onClose }) {
  const heading = useId();
  const copies = state.copies || EMPTY_COPIES;
  const [details, setDetails] = useState(null);
  const [consent, setConsent] = useState(null);
  const [pending, setPending] = useState(null);
  const [failure, setFailure] = useState(null);
  const latest = useRef(state);
  const active = useRef(null);
  const mounted = useRef(false);
  const discardTrigger = useRef(null);
  latest.current = state;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { setConsent(null); setFailure(null); }, [state]);
  useEffect(() => { setDetails(null); }, [copies]);
  const busy = !!state.loading || !!state.busy || pending?.state === state;
  const validCopy = (copy) => copies.includes(copy) && !isLegacy(copy);
  const selected = details?.copies === copies && validCopy(details.copy) ? details.copy : null;
  const selection = consent?.state === state && validCopy(consent.copy)
    && (consent.tabOnly ? consent.copy.canDiscardTab : consent.copy.canDiscard) ? consent : null;
  const invoke = async (callback, copy) => {
    if (!callback || busy || latest.current !== state || active.current?.state === state) return;
    const action = { state };
    active.current = action; setPending(action); setFailure(null);
    try { await callback(copy); }
    catch (error) {
      if (mounted.current && latest.current === state) setFailure({ state,
        message: error?.message || 'The action could not be completed. Refresh and review the saved copies.' });
    } finally {
      if (active.current === action) active.current = null;
      if (mounted.current) setPending((value) => value === action ? null : value);
    }
  };
  return <section aria-labelledby={heading} aria-busy={busy} className="w-full min-w-0 space-y-3 rounded-lg border p-3">
    <h2 id={heading} className="text-lg font-semibold">Saved delivery work</h2>
    <p className="text-sm">Open a draft to continue, check a delivery, or delete a copy you no longer need. Nothing is sent from this screen.</p>
    {state.loading && <p role="status">Loading saved work…</p>}
    {busy && !state.loading && <p role="status">Working… Keep this view open until the action finishes.</p>}
    {state.message && <p role="status" className="text-sm">{state.message}</p>}
    {state.error && <p role="alert" className="text-sm">{state.error}</p>}
    {failure?.state === state && <p role="alert" className="text-sm">{failure.message}</p>}
    {!state.loading && !copies.length && <p>No saved copies to review.</p>}
    <ul className="space-y-3" aria-label="Local delivery copies">
      {copies.map((copy, index) => {
        const legacy = isLegacy(copy);
        const expanded = selected === copy;
        const status = copy.legacyAttempted ? 'submission result unknown' : copy.attempt?.status || 'not submitted';
        return <li key={copy.id} className="min-w-0 rounded-lg border p-3" aria-label={`Local copy ${index + 1}`}>
          <h3 className="font-medium">Copy {index + 1} — {sourceLabel(copy)}</h3>
          {legacy ? <p className="text-sm">Older saved copy; ownership and submission result are not established. Its details and media are not shown here.</p> : <>
            <p className="text-sm">{(copy.fields?.photos || []).length} {(copy.fields?.photos || []).length === 1 ? 'photo' : 'photos'} · {status === 'not submitted' ? 'Not sent' : status === 'accepted' ? 'Received by the app' : 'Delivery not confirmed'}</p>
            {copy.source === 'tab' && <p className="text-sm">May include unsaved changes. Keep this tab open until saving is confirmed.</p>}
            {copy.legacyAttempted && <p className="text-sm">This older failure report may have been sent, but has no submission identifier. It is retained; it cannot be resent, checked or discarded here.</p>}
            {operation(copy) === 'failure' && status === 'accepted' && <p className="text-sm font-medium">Failure report accepted. These local photos were NOT uploaded.</p>}
            {copy.attempt && <p className="text-sm">Checking tells you whether this delivery arrived. It does not send it again.</p>}
            <div className="mt-2 flex flex-wrap gap-2">
              <button type="button" className={buttonClass} disabled={busy} aria-expanded={expanded}
                onClick={() => { if (!busy) setDetails(expanded ? null : { copies, copy }); }}>{expanded ? 'Hide details' : 'Show details'}</button>
              <button type="button" className={buttonClass} disabled={busy || !copy.canSelect || !onSelect}
                onClick={() => { if (copy.canSelect && validCopy(copy)) invoke(onSelect, copy); }}>Open delivery</button>
              {!!copy.attempt && !!onCheckReceipt && <button type="button" className={buttonClass}
                disabled={busy || !copy.canCheckReceipt} onClick={() => {
                  if (copy.canCheckReceipt && validCopy(copy)) invoke(onCheckReceipt, copy);
                }}>Check again</button>}
              <button type="button" className={buttonClass} disabled={busy || !copy.canDiscard || !onDiscard}
                onClick={(event) => {
                  if (busy || !copy.canDiscard || !validCopy(copy)) return;
                  discardTrigger.current = event.currentTarget; setConsent({ state, copy, number: index + 1 });
                }}>Delete saved copy</button>
              {copy.canDiscardTab && <button type="button" className={buttonClass} disabled={busy || !onDiscardTab}
                onClick={(event) => {
                  if (busy || !copy.canDiscardTab || !validCopy(copy)) return;
                  discardTrigger.current = event.currentTarget; setConsent({ state, copy, number: index + 1, tabOnly: true });
                }}>Discard this tab copy only</button>}
            </div>
            {expanded && <CopyDetails copy={copy} />}
          </>}
        </li>;
      })}
    </ul>
    <div className="flex flex-wrap gap-2">
      <button type="button" className={buttonClass} disabled={busy || !state.canKeepBoth || !onKeepBoth}
        onClick={() => { if (state.canKeepBoth) invoke(onKeepBoth); }}>Keep both</button>
      <button type="button" className={buttonClass} disabled={busy || !onRefresh} onClick={() => invoke(onRefresh)}>Refresh</button>
      <button type="button" className={buttonClass} disabled={busy || !state.canClose || !onClose}
        onClick={() => { if (state.canClose) invoke(onClose); }}>Close</button>
    </div>
    {!state.canClose && !state.loading && <p className="text-sm">Keep this tab open; work is not yet safe to close. Follow the message above or refresh.</p>}
    <AlertDialog open={!!selection && !state.loading && !state.busy} onOpenChange={(open) => { if (!open) setConsent(null); }}>
      <AlertDialogContent onCloseAutoFocus={(event) => { event.preventDefault(); discardTrigger.current?.focus(); }}>
          <AlertDialogTitle>{selection?.tabOnly ? 'Delete this unsaved copy?' : 'Delete saved copy?'}</AlertDialogTitle>
        <AlertDialogDescription>
          Copy {selection?.number} — {selection ? sourceLabel(selection.copy) : ''}. {selection?.tabOnly
            ? 'Only its current running-tab details and photos will be removed. Saved device copies remain unchanged.'
            : 'Delete this saved copy and its photos from this device? Anything that hasn’t reached the app will be lost. Any completed delivery will stay unchanged.'}
          Other saved copies and photos in your camera roll will stay unchanged.
        </AlertDialogDescription>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Keep copy</AlertDialogCancel>
          <AlertDialogAction disabled={busy} className="bg-red-700 hover:bg-red-800" onClick={(event) => {
            event.preventDefault();
            if (!selection || busy || latest.current !== selection.state) return;
            setConsent(null); invoke(selection.tabOnly ? onDiscardTab : onDiscard, selection.copy);
          }}>Delete copy</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </section>;
}
